import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Delete as DeleteIcon,
  MarkEmailRead as MarkReadIcon,
  MarkEmailUnread as MarkUnreadIcon,
  Phone as PhoneIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import voicemailService from '../../services/voicemail.service';
import { Voicemail as VoicemailType } from '../../types/voicemail.types';
import { tokenManager } from '../../services/api';

const Voicemail: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [voicemails, setVoicemails] = useState<VoicemailType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [playingId, setPlayingId] = useState<number | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedVoicemail, setSelectedVoicemail] = useState<VoicemailType | null>(null);

  useEffect(() => {
    fetchVoicemails();

    // Cleanup audio on unmount
    return () => {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.src = '';
      }
      // Clean up blob URL on unmount
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchVoicemails = async () => {
    try {
      setLoading(true);
      const response = await voicemailService.getVoicemails();
      if (response.success) {
        setVoicemails(response.data.voicemails);
      }
      setError(null);
    } catch (err) {
      setError('Failed to load voicemails');
      console.error('Voicemail error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePlayPause = async (voicemail: VoicemailType) => {
    if (playingId === voicemail.id) {
      // Pause current audio
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
      // Clean up blob URL
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      setPlayingId(null);
      setAudioUrl(null);
    } else {
      // Stop previous audio if any
      if (playingId) {
        const audio = audioRef.current;
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
        }
        // Clean up previous blob URL
        if (audioUrl) {
          URL.revokeObjectURL(audioUrl);
        }
      }

      try {
        // Fetch audio with authentication and create blob URL
        const token = tokenManager.getAccessToken();
        const response = await fetch(voicemailService.getAudioUrl(voicemail.id), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch audio: ${response.status}`);
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);

        setAudioUrl(url);
        setPlayingId(voicemail.id);

        // Mark as read when played
        if (!voicemail.is_read) {
          handleMarkRead(voicemail, true);
        }
      } catch (err) {
        console.error('Error playing audio:', err);
        setError('Failed to play voicemail');
        setPlayingId(null);
        setAudioUrl(null);
      }
    }
  };

  const handleMarkRead = async (voicemail: VoicemailType, isRead: boolean) => {
    try {
      await voicemailService.markAsRead(voicemail.id, isRead);
      setVoicemails(prev =>
        prev.map(vm =>
          vm.id === voicemail.id ? { ...vm, is_read: isRead } : vm
        )
      );
      // Trigger a custom event to update the sidebar badge
      window.dispatchEvent(new Event('voicemail-updated'));
    } catch (err) {
      console.error('Error marking voicemail:', err);
      setError('Failed to update voicemail status');
    }
  };

  const handleDelete = async () => {
    if (!selectedVoicemail) return;

    try {
      await voicemailService.deleteVoicemail(selectedVoicemail.id);
      setVoicemails(prev => prev.filter(vm => vm.id !== selectedVoicemail.id));
      setDeleteDialogOpen(false);
      setSelectedVoicemail(null);

      // Stop audio if playing
      if (playingId === selectedVoicemail.id) {
        const audio = audioRef.current;
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
        }
        // Clean up blob URL
        if (audioUrl) {
          URL.revokeObjectURL(audioUrl);
        }
        setPlayingId(null);
        setAudioUrl(null);
      }

      // Trigger a custom event to update the sidebar badge
      window.dispatchEvent(new Event('voicemail-updated'));
    } catch (err) {
      console.error('Error deleting voicemail:', err);
      setError('Failed to delete voicemail');
    }
  };

  const formatDuration = (seconds: string): string => {
    const secs = parseInt(seconds);
    const mins = Math.floor(secs / 60);
    const remainingSecs = secs % 60;
    return `${mins}:${remainingSecs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffHours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Voicemail
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Total: {voicemails.length} | Unread: {voicemails.filter(vm => !vm.is_read).length}
      </Typography>

      {voicemails.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No voicemails found
          </Typography>
        </Paper>
      ) : (
        <>
          {isMobile ? (
            // Mobile Card View
            <Box>
              {voicemails.map((voicemail) => (
                <Card key={voicemail.id} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="start">
                      <Box>
                        <Typography variant="subtitle1" fontWeight={voicemail.is_read ? 'normal' : 'bold'}>
                          {voicemail.mailbox_name} ({voicemail.extension})
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1} mt={1}>
                          <PhoneIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {voicemail.caller_id}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                          <TimeIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(voicemail.date)} • {formatDuration(voicemail.duration)}
                          </Typography>
                        </Box>
                      </Box>
                      {!voicemail.is_read && (
                        <Chip label="New" color="primary" size="small" />
                      )}
                    </Box>
                  </CardContent>
                  <CardActions>
                    <IconButton
                      color="primary"
                      onClick={() => handlePlayPause(voicemail)}
                    >
                      {playingId === voicemail.id ? <PauseIcon /> : <PlayIcon />}
                    </IconButton>
                    <IconButton
                      onClick={() => handleMarkRead(voicemail, !voicemail.is_read)}
                    >
                      {voicemail.is_read ? <MarkUnreadIcon /> : <MarkReadIcon />}
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => {
                        setSelectedVoicemail(voicemail);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              ))}
            </Box>
          ) : (
            // Desktop Table View
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell>Date/Time</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {voicemails.map((voicemail) => (
                    <TableRow key={voicemail.id}>
                      <TableCell>
                        {!voicemail.is_read && (
                          <Chip label="New" color="primary" size="small" />
                        )}
                      </TableCell>
                      <TableCell>{voicemail.caller_id}</TableCell>
                      <TableCell>
                        {voicemail.mailbox_name} ({voicemail.extension})
                      </TableCell>
                      <TableCell>{formatDate(voicemail.date)}</TableCell>
                      <TableCell>{formatDuration(voicemail.duration)}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="primary"
                          onClick={() => handlePlayPause(voicemail)}
                          title={playingId === voicemail.id ? "Pause" : "Play"}
                        >
                          {playingId === voicemail.id ? <PauseIcon /> : <PlayIcon />}
                        </IconButton>
                        <IconButton
                          onClick={() => handleMarkRead(voicemail, !voicemail.is_read)}
                          title={voicemail.is_read ? "Mark as unread" : "Mark as read"}
                        >
                          {voicemail.is_read ? <MarkUnreadIcon /> : <MarkReadIcon />}
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => {
                            setSelectedVoicemail(voicemail);
                            setDeleteDialogOpen(true);
                          }}
                          title="Delete"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Voicemail?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this voicemail? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Audio Player */}
      {audioUrl && playingId && (
        <audio
          ref={audioRef}
          src={audioUrl}
          controls={isMobile}
          autoPlay
          onLoadedMetadata={(e) => {
            const target = e.currentTarget;
            target.volume = 1.0;
            target.muted = false;
          }}
          onEnded={() => {
            // Clean up blob URL
            if (audioUrl) {
              URL.revokeObjectURL(audioUrl);
            }
            setPlayingId(null);
            setAudioUrl(null);
          }}
          onError={(e) => {
            console.error('Audio playback error:', e);
            setError('Failed to play voicemail');
            // Clean up blob URL
            if (audioUrl) {
              URL.revokeObjectURL(audioUrl);
            }
            setPlayingId(null);
            setAudioUrl(null);
          }}
          style={isMobile ? {
            width: '100%',
            position: 'fixed',
            bottom: 60,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1300,
            maxWidth: 'calc(90% - 32px)',
            backgroundColor: '#fff',
            boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
            borderRadius: '4px',
            padding: '4px'
          } : {
            display: 'none'
          }}
        />
      )}
    </Box>
  );
};

export default Voicemail;